/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unsafe-return */
import { useCallback, useId, useMemo, useState } from "react";

import { Box } from "@mui/material";
import { MultiAutocompleteHookForm, Typography } from "components";
import { useSearchAutoComplete } from "hooks";
import { useAuth } from "hooks/useAuth";
import uniqBy from "lodash/unionBy";
import { useQuery } from "react-query";
import { getOptionsSelect, rem } from "utils";

import { ModelName } from "./types";
import { getServices } from "./utils";

interface Props {
  label?: string;
  subLabel?: string;
  placeholder?: string;
  name: string;
  required?: boolean;
  disabled?: boolean;
  model: ModelName;
  keyValue?: string;
  keyLabel?: string;
  keySubLabel?: string;
  exclude?: string[];
  depField?: string | number;
  useDepField?: boolean;
  filterLocal?: boolean;
}

export const renderCustomOption = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data: any,
  keyLabel: string,
  keySubLabel: string
) => {
  return (
    <Box
      display="flex"
      alignItems="center"
      gap={rem(8)}
      overflow="hidden"
      flexWrap="wrap"
    >
      <Typography type="TextSM" fontWeight={500}>
        {data?.[keyLabel]}
      </Typography>
      <Typography type="TextXS">({data?.[keySubLabel]})</Typography>
    </Box>
  );
};

const defaultPage = 1;
const defaultLimit = 50;

type ShortItem = {
  id: number | string;
  code: string;
  name: string;
  nativeName?: string;
};

const MultiAutocompleteHookFormAsync = (props: Props) => {
  const {
    label,
    placeholder,
    name,
    required,
    model = "employee",
    disabled,
    keyValue = "id",
    keyLabel = "name",
    keySubLabel,
    exclude = [],
    depField,
    useDepField,
    filterLocal = false,
  } = props;
  const queryKey = useId();

  const [data, setData] = useState<ShortItem[]>([]);
  const [hasMore, setHasMore] = useState(true);
  const { userAppRole, organizations, apps } = useAuth();

  const searchAutocomplete = useSearchAutoComplete<string>(undefined);
  const enableQuery = useMemo(() => {
    if (userAppRole === "Employee") {
      if (["organization", "application"]?.includes(model)) {
        return false;
      }
    }
    if (useDepField) {
      return !!depField && searchAutocomplete.isFocus;
    }
    return searchAutocomplete.isFocus;
  }, [depField, model, searchAutocomplete.isFocus, useDepField, userAppRole]);

  const { isLoading } = useQuery({
    queryKey: [
      queryKey,
      searchAutocomplete.keyword,
      depField,
      searchAutocomplete.page,
    ],
    enabled: enableQuery,
    queryFn: () =>
      getServices(
        model,
        searchAutocomplete.page,
        searchAutocomplete.keyword,
        depField
      ),
    onSuccess: (res) => {
      if (searchAutocomplete.page === defaultPage) {
        setData(res?.data?.items as ShortItem[]);
      } else {
        setData((prevData) =>
          uniqBy([...prevData, ...(res?.data?.items || [])], keyValue)
        );
      }
      if (res?.data?.items?.length === defaultLimit) {
        setHasMore(true);
      } else {
        setHasMore(false);
      }
    },
  });

  const options = useMemo(() => {
    let optionsFilter = data;
    if (userAppRole === "Employee") {
      if (["organization", "application"]?.includes(model)) {
        if (model === "organization") {
          optionsFilter = organizations as ShortItem[];
        }
        if (model === "application")
          optionsFilter = apps as unknown as ShortItem[];
      }
    }
    let optionFilters = [
      ...getOptionsSelect<string>({
        data: optionsFilter || [],
        keyLabel,
        keyValue,
        keySubLabel,
      }).filter((item) => !exclude?.includes(String(item.value))),
    ];
    if (filterLocal && searchAutocomplete.keyword) {
      optionFilters = optionFilters.filter((option) =>
        option.label
          .toLowerCase()
          .includes(searchAutocomplete.keyword.toLowerCase())
      );
    }
    return optionFilters;
  }, [
    data,
    userAppRole,
    keyLabel,
    keyValue,
    keySubLabel,
    filterLocal,
    searchAutocomplete.keyword,
    model,
    apps,
    organizations,
    exclude,
  ]);

  const handleLoadMore = useCallback(() => {
    if (hasMore && !isLoading) {
      searchAutocomplete.setPage((prevPage) => prevPage + 1);
    }
  }, [hasMore, isLoading, searchAutocomplete]);

  return (
    <MultiAutocompleteHookForm
      name={name}
      label={label}
      placeholder={placeholder}
      options={options}
      onInputChange={searchAutocomplete.setKeyword}
      onFocus={searchAutocomplete.onFocus}
      onBlur={searchAutocomplete.onBlur}
      required={required}
      disabled={disabled}
      loading={isLoading}
      onLoadMore={handleLoadMore}
      {...(keySubLabel && {
        renderOption: (_, option) => {
          return renderCustomOption(
            option.original,
            String(keyLabel || ""),
            keySubLabel
          );
        },
      })}
    />
  );
};

export default MultiAutocompleteHookFormAsync;
